/* ==========================================================================
   H2Glow Glow Rewards — buy 6 bags, the 7th is free

   No dependencies. Load with `defer` after glow-card.liquid has rendered.
   Liquid paints the correct initial state server-side; this file only
   mutates it, so there is no flash and no duplicated markup.

   Public API (see INTEGRATION.md §3):
     window.H2GlowCard.set(n)      update the stamp count and animate
     window.H2GlowCard.open()      open the panel (pinned)
     window.H2GlowCard.close()     collapse to the ring
     window.H2GlowCard.get()       -> { earned, goal, signedIn }
   ========================================================================== */

(function () {
  'use strict';

  var root = document.querySelector('[data-h2g-card]');
  if (!root) return;

  var toggle   = root.querySelector('[data-h2g-toggle]');
  var closeBtn = root.querySelector('[data-h2g-close]');
  var titleEl  = root.querySelector('[data-h2g-title]');
  var bodyEl   = root.querySelector('[data-h2g-body]');
  var slotsEl  = root.querySelector('[data-h2g-slots]');
  var arcEl    = root.querySelector('[data-h2g-arc]');
  var badgeEl  = root.querySelector('[data-h2g-badge]');
  var miniBag  = root.querySelector('.h2g-card__mini .h2g-bag');

  var COPY = { titles: [], bodies: [], guest: null };
  try {
    var raw = root.querySelector('[data-h2g-copy]');
    if (raw) COPY = JSON.parse(raw.textContent);
  } catch (e) {
    /* Copy block failed to parse — the server-rendered text stays as-is. */
  }

  var ARC_LENGTH = 141.37;               /* 2 * PI * r, r = 22.5 */
  var SCROLL_HIDE_PX = 40;               /* cumulative down-scroll before tucking */
  var PEEK_MS = 2500;                    /* how long a scroll-up peek lasts */
  var AUTO_OPEN_DELAY_MS = 6000;
  var AUTO_OPEN_MIN_VIEWS = 2;           /* never on the first pageview */
  var AUTO_OPEN_COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

  var KEY_VIEWS = 'h2g_card_views';
  var KEY_LAST_AUTO = 'h2g_card_last_auto_open';

  var state = {
    earned:   parseInt(root.dataset.earned, 10) || 0,
    goal:     parseInt(root.dataset.goal, 10) || 6,
    signedIn: root.dataset.signedIn === 'true',
    open:     false,
    pinned:   false
  };

  var peekTimer = null;
  var autoTimer = null;
  var lastY = window.pageYOffset || 0;
  var downDelta = 0;
  var ticking = false;

  /* ------------------------------------------------------------ storage --
     Private windows and locked-down browsers throw on access. Never let a
     storage failure take the widget down with it. */
  function store(area, key, value) {
    try {
      if (value === undefined) return window[area].getItem(key);
      window[area].setItem(key, value);
    } catch (e) { return null; }
  }

  /* -------------------------------------------------------------- render */
  function isReward() { return state.earned >= state.goal; }

  function render() {
    var i = Math.min(Math.max(state.earned, 0), state.goal);
    var reward = isReward();

    /* copy */
    if (COPY.titles && COPY.titles.length) {
      if (state.signedIn) {
        if (titleEl) titleEl.textContent = COPY.titles[i];
        if (bodyEl)  bodyEl.textContent  = COPY.bodies[i];
      } else if (COPY.guest) {
        if (titleEl) titleEl.textContent = COPY.guest.title;
        if (bodyEl)  bodyEl.textContent  = COPY.guest.body;
      }
    }

    /* seven slots — the last one is the prize, never "earned" */
    if (slotsEl) {
      var slots = slotsEl.children;
      for (var s = 0; s < slots.length; s++) {
        var isRewardSlot = (s === slots.length - 1);
        var filled = !isRewardSlot && s < state.earned;
        slots[s].classList.toggle('is-ready', isRewardSlot && reward);
        setBag(slots[s].querySelector('.h2g-bag'), filled);
      }
    }

    /* arc + trigger */
    if (arcEl) {
      arcEl.setAttribute('stroke-dashoffset', (ARC_LENGTH - (i / state.goal) * ARC_LENGTH).toFixed(2));
      arcEl.setAttribute('stroke', reward ? '#FECB7E' : '#61457C');
    }
    setBag(miniBag, state.earned > 0);
    if (badgeEl) badgeEl.hidden = !reward;
    if (toggle) toggle.setAttribute('aria-label', 'Glow Rewards, ' + state.earned + ' of ' + state.goal + ' bags');

    root.dataset.earned = String(state.earned);
  }

  function setBag(svg, filled) {
    if (!svg) return;
    svg.classList.toggle('is-off', !filled);
    var use = svg.querySelector('use');
    if (use) use.setAttribute('href', filled ? '#h2g-bag-on' : '#h2g-bag-off');
  }

  /* ---------------------------------------------------------- open/close */
  function setOpen(next, pin) {
    state.open = next;
    state.pinned = pin === true ? true : (next ? state.pinned : false);
    root.dataset.open = String(next);
    if (toggle) toggle.setAttribute('aria-expanded', String(next));
    if (next) root.dataset.tucked = 'false';
    root.dataset.yield = 'false';
    if (!next) queueYield();
  }

  function open(pin)  { clearTimeout(peekTimer); setOpen(true, pin !== false); }
  function close()    { clearTimeout(peekTimer); setOpen(false, false); }

  /* ------------------------------------------------------------ autohide */
  function onScroll() {
    if (ticking) return;
    ticking = true;
    window.requestAnimationFrame(function () {
      var y = window.pageYOffset || document.documentElement.scrollTop || 0;
      var delta = y - lastY;

      if (delta > 0) {
        downDelta += delta;
        if (downDelta > SCROLL_HIDE_PX) {
          /* Reading wins. Tuck whatever is showing. */
          clearTimeout(peekTimer);
          if (state.open) setOpen(false, false);
          state.pinned = false;
          root.dataset.tucked = 'true';
        }
      } else if (delta < 0) {
        downDelta = 0;
        root.dataset.tucked = 'false';   /* scroll up brings the ring back — but never auto-opens the panel */
      }

      lastY = y;
      ticking = false;
    });
  }

  /* ------------------------------------------------------ yield to page --
     A closed ring never sits on top of something tappable. When a link,
     button or field is under the ring, it steps back (data-yield) and lets
     taps through until the spot is clear again. */
  var TAPPABLE = 'a[href], button, input, select, textarea, label, summary, [role="button"]';
  var yieldQueued = false;

  function underRingIsTappable() {
    var r = toggle.getBoundingClientRect();
    if (!r.width) return false;
    var cx = r.left + r.width / 2, cy = r.top + r.height / 2, e = 8;
    var points = [[cx, cy], [r.left + e, cy], [r.right - e, cy], [cx, r.top + e], [cx, r.bottom - e]];
    for (var p = 0; p < points.length; p++) {
      var stack = document.elementsFromPoint(points[p][0], points[p][1]);
      for (var i = 0; i < stack.length; i++) {
        if (root.contains(stack[i])) continue;
        if (stack[i].closest && stack[i].closest(TAPPABLE)) return true;
        break;   /* only the topmost page element under this point matters */
      }
    }
    return false;
  }

  function checkYield() {
    yieldQueued = false;
    if (!toggle) return;
    var next = !state.open && underRingIsTappable();
    root.dataset.yield = String(next);
  }

  function queueYield() {
    if (yieldQueued) return;
    yieldQueued = true;
    window.requestAnimationFrame(checkYield);
  }

  /* ----------------------------------------------------- once-per-session */
  function scheduleAutoOpen() {
    var views = (parseInt(store('sessionStorage', KEY_VIEWS), 10) || 0) + 1;
    store('sessionStorage', KEY_VIEWS, String(views));

    if (views < AUTO_OPEN_MIN_VIEWS) return;         /* never on arrival */

    var last = parseInt(store('localStorage', KEY_LAST_AUTO), 10) || 0;
    var cooled = (Date.now() - last) > AUTO_OPEN_COOLDOWN_MS;

    /* The reward state earns one prompt per session regardless of cooldown. */
    if (!cooled && !isReward()) return;

    autoTimer = setTimeout(function () {
      if (state.open) return;
      open(false);                                    /* not pinned — it can tuck */
      store('localStorage', KEY_LAST_AUTO, String(Date.now()));
      clearTimeout(peekTimer);
      peekTimer = setTimeout(function () {
        if (!state.pinned) setOpen(false, false);
      }, PEEK_MS * 2);
    }, AUTO_OPEN_DELAY_MS);
  }

  /* -------------------------------------------------------------- events */
  if (toggle) {
    toggle.addEventListener('click', function () {
      clearTimeout(autoTimer);
      if (state.open) close(); else open(true);
    });
  }
  if (closeBtn) closeBtn.addEventListener('click', close);

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && state.open) {
      close();
      if (toggle) toggle.focus();
    }
  });

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('scroll', queueYield, { passive: true });
  window.addEventListener('resize', queueYield);
  window.addEventListener('load', queueYield);
  /* Layout can move under a still page (images, accordions, sticky bars). */
  setInterval(function () { if (!document.hidden) queueYield(); }, 700);

  /* The theme fires this after a successful add-to-cart. See INTEGRATION.md §3. */
  document.addEventListener('h2glow:card:update', function (e) {
    if (!e.detail || typeof e.detail.earned !== 'number') return;
    api.set(e.detail.earned);
  });

  /* ----------------------------------------------------------------- api */
  var api = {
    set: function (n) {
      state.earned = Math.min(Math.max(parseInt(n, 10) || 0, 0), state.goal);
      render();   /* update the ring quietly — the panel only opens on a click */
      return state.earned;
    },
    open:  function () { open(true); },
    close: close,
    get:   function () {
      return { earned: state.earned, goal: state.goal, signedIn: state.signedIn };
    }
  };
  window.H2GlowCard = api;

  /* ---------------------------------------------------------------- boot */
  render();
  setOpen(false, false);   /* launch COLLAPSED — the ring only; a click expands the card */
  root.dataset.tucked = 'false';
  queueYield();
})();
